import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

interface SessionExpiryBannerProps {
  /** ISO instant the reservation lapses. */
  expiresAt: string;
}

/** Minutes left below which the banner appears. A 3-hour reservation needs
 *  no countdown for most of its life; it needs one late enough that the
 *  operator can finish or reopen. */
const WARN_BELOW_MINUTES = 30;

/** Warns when a reservation is close to lapsing. After expiry the rows go
 *  back to whoever wants them, so an operator mid-batch needs to know
 *  before it happens rather than discover a save conflict.
 *
 *  Renders nothing while there is plenty of time left. */
export function SessionExpiryBanner({ expiresAt }: SessionExpiryBannerProps) {
  const [now, setNow] = useState(() => Date.now());

  const expiry = new Date(expiresAt).getTime();
  const valid = Number.isFinite(expiry);

  // A minute is fine: the banner communicates urgency, not a countdown to
  // the second, and re-rendering the whole card every second would be a
  // needless cost during data entry.
  useEffect(() => {
    if (!valid) return;
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [valid]);

  if (!valid) return null;

  const minutesLeft = Math.floor((expiry - now) / 60_000);
  if (minutesLeft > WARN_BELOW_MINUTES) return null;

  const expired = minutesLeft <= 0;

  return (
    <div
      className={
        expired
          ? "flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3"
          : "flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
      }
    >
      <Clock
        className={
          expired
            ? "mt-0.5 h-4 w-4 shrink-0 text-rose-600"
            : "mt-0.5 h-4 w-4 shrink-0 text-amber-600"
        }
      />
      <p className={expired ? "text-sm text-rose-800" : "text-sm text-amber-900"}>
        {expired ? (
          <>
            This reservation has lapsed. The rows you have not saved are open
            to other operators again. Finish the one on screen, then start a
            new session to reserve what is left.
          </>
        ) : (
          <>
            {minutesLeft} minute{minutesLeft === 1 ? "" : "s"} left on this
            reservation. Rows you have not saved by then go back to the queue.
          </>
        )}
      </p>
    </div>
  );
}
